import type { ComponentPropsWithoutRef, FC } from 'react'

import { AssetFallbackIcon } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { Td } from '@skytech/ui/Table/Td'
import { TrButton } from '@skytech/ui/Table/TrButton'
import CheckboxBlankOutlineIcon from 'mdi-react/CheckboxBlankOutlineIcon'
import CheckboxMarkedOutlineIcon from 'mdi-react/CheckboxMarkedOutlineIcon'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LocalDate } from '@/components/LocalDate'
import { TrLink } from '@/components/TrLink'
import { USER_ROLE, useIsAdmin, useIsItPurchaser, useIsThreeStepIT } from '@/hooks/access'
import useUser from '@/hooks/user'
import type { Asset, AssetUserType } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import { AssetLink } from './AssetLink'
import { badgeTypeByColor, getLeaseBadgeInfo } from './helper'

type ConditionalTrWrapperProps = {
  id: number
  showPopup?: boolean
  isSelected?: boolean
  onClick?: (id: number) => void
} & Omit<ComponentPropsWithoutRef<'tr'>, 'id'>

const ConditionalTrWrapper: FC<ConditionalTrWrapperProps> = ({
  children,
  id,
  showPopup = false,
  isSelected,
  onClick,
  ...props
}) => {
  const isAdmin = useIsAdmin()
  const isItPurchaser = useIsItPurchaser()
  const user = useUser()

  if (showPopup && onClick) {
    return (
      <TrButton onClick={() => onClick(id)} className={`${isSelected ? 'selected' : ''}`}>
        {children}
      </TrButton>
    )
  }

  if (isAdmin || user?.role === USER_ROLE.IT_USER || isItPurchaser) {
    return <TrLink path={APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id))}>{children}</TrLink>
  }
  return <tr {...props}>{children}</tr>
}

export const AssetListItem: FC<
  Asset & {
    leaseCurrentEndingOption?: string
    searchFilter: string
    isLeasedOwnershipType: boolean
    showLeasedFeatures: boolean
    isSelected?: boolean
    onSetAssetLinkPopupOpen: (open: boolean) => void
    onSetAssetLink: (link: string) => void
    onSelectLeasedAsset: (id: number) => void
  }
> = ({
  costObjectName,
  imageUrl,
  assetDescription,
  id,
  costObjectId,
  received,
  leaseStart,
  leaseEnd,
  cost,
  address,
  addressCity,
  addressZip,
  status,
  userType,
  imei,
  snr,
  assetType,
  assetCurrency,
  leaseCurrentEndingOption,
  searchFilter,
  isLeasedOwnershipType,
  showLeasedFeatures,
  isSelected,
  onSetAssetLink,
  onSetAssetLinkPopupOpen,
  onSelectLeasedAsset,
}) => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()
  const user = useUser()

  return (
    <ConditionalTrWrapper
      id={id}
      showPopup={showLeasedFeatures}
      isSelected={isSelected}
      onClick={() => onSelectLeasedAsset(id)}
    >
      {showLeasedFeatures && (
        <Td className="center p-1 pr-0 md:table-cell md:p-1 md:pr-0 lg:p-2 lg:pr-0 xl:p-3">
          {isSelected ? <CheckboxMarkedOutlineIcon /> : <CheckboxBlankOutlineIcon />}
        </Td>
      )}
      <Td className="hidden md:p-1 lg:table-cell lg:p-2 xl:p-3">
        <div className="asset-name">
          <ImageWithIconFallback
            alt="asset"
            src={imageUrl || ''}
            className="hidden md:table-cell"
            fallbackIcon={
              <div className="shadow-basic flex h-[3.7rem] w-[3.7rem] min-w-[3.7rem] max-w-full items-center justify-center overflow-hidden rounded-full bg-white p-1 shadow transition duration-200 ease-in-out max-[500px]:h-12 max-[500px]:w-12 sm:h-[3.7rem] sm:w-[3.7rem]">
                <AssetFallbackIcon assetType={assetType} />
              </div>
            }
          />
          <AssetCategoryIcon userType={userType} />
        </div>
      </Td>

      <Td className="flex flex-col">
        <div className="whitespace-normal break-words">
          {showLeasedFeatures ? (
            <AssetLink
              className="whitespace-normal break-words text-start"
              searchFilter={searchFilter}
              assetText={assetDescription}
              showPopup={showLeasedFeatures}
              isLinkValid={(isAdmin || user?.role === USER_ROLE.IT_USER) && showLeasedFeatures}
              link={APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id))}
              onAssetClick={() => {
                onSetAssetLink(APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id)))
                onSetAssetLinkPopupOpen(true)
              }}
            />
          ) : (
            <Highlight pattern={searchFilter} text={assetDescription} />
          )}
        </div>
        {costObjectName && (
          <div className="table-cell text-[0.8rem] text-gray-400 md:hidden">
            <Highlight pattern={searchFilter} text={costObjectName} />
          </div>
        )}
        {isLeasedOwnershipType && cost !== null && cost > 0 && (
          <div className="table-cell text-[0.8rem] text-gray-400 md:hidden">
            <FormattedNumberWithCurrency amount={cost} currency={assetCurrency} />
          </div>
        )}
        <div className="hidden flex-col gap-1 md:flex">
          <div className="mt-[0.2em] hidden text-[0.8rem] text-gray-400 md:table-cell">
            <Highlight pattern={searchFilter} text={translate(`asset_type/${assetType}`)} />
          </div>
          {imei && (
            <div className="mt-[0.2em] hidden text-[0.8rem] text-gray-400 md:table-cell">
              <Translation id="IMEI" />: <Highlight pattern={searchFilter} text={imei} />
            </div>
          )}
          {snr && (
            <div className="mt-[0.2em] hidden text-[0.8rem] text-gray-400 md:table-cell">
              <Translation id="S/N" />: <Highlight pattern={searchFilter} text={snr} />
            </div>
          )}
        </div>
      </Td>

      <Td className="hidden md:table-cell md:p-1 lg:p-2 xl:p-3">
        <div>
          {costObjectName === 'Expired' ? (
            <Translation id="User unavailable" />
          ) : (
            <AssetLink
              searchFilter={searchFilter}
              assetText={costObjectName}
              showPopup={isLeasedOwnershipType}
              isLinkValid={isAdmin || user?.role === USER_ROLE.IT_USER}
              link={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId))}
              onAssetClick={() => {
                onSetAssetLink(APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId)))
                onSetAssetLinkPopupOpen(true)
              }}
            />
          )}
        </div>
        <Highlight pattern={searchFilter} text={translate(`asset_user_type/${userType}`)} />
      </Td>

      <Td className="hidden md:table-cell md:p-1 lg:p-2 xl:p-3">
        <Highlight pattern={searchFilter} text={translate(`asset_status/${status}`)} />
      </Td>

      <Td
        className={twJoin(
          'md:p-1 lg:p-2 xl:p-3',
          isLeasedOwnershipType ? 'hidden xl:table-cell' : 'hidden lg:table-cell'
        )}
      >
        <Highlight pattern={searchFilter} text={address} />
        <div className="mt-[0.2em] text-[0.8rem] text-gray-400">
          <Highlight pattern={searchFilter} text={addressZip} />{' '}
          <Highlight pattern={searchFilter} text={addressCity} />
        </div>
      </Td>

      {!isLeasedOwnershipType && (
        <Td className="hidden md:table-cell md:p-1 lg:p-2 xl:p-3">
          <LocalDate date={received} />
        </Td>
      )}

      {isLeasedOwnershipType && (
        <Td className="hidden sm:table-cell md:p-1 lg:p-2 xl:p-3">
          <LocalDate date={leaseStart} />
        </Td>
      )}
      {isLeasedOwnershipType && (
        <AssetLeaseEndInfoTd
          leaseEnd={leaseEnd}
          leaseCurrentEndingOption={leaseCurrentEndingOption}
        />
      )}

      <Td className="hidden md:table-cell md:p-1 lg:p-2 xl:p-3">
        {cost !== null && cost > 0 ? (
          <FormattedNumberWithCurrency amount={cost} currency={assetCurrency} />
        ) : (
          <span> — </span>
        )}
      </Td>
    </ConditionalTrWrapper>
  )
}

const AssetCategoryIcon: FC<{ userType: AssetUserType }> = ({ userType }) => {
  if (!['GREENPOOL', 'POOL'].includes(userType)) return null
  const isThreeStepIT = useIsThreeStepIT()

  return (
    <div className="duration-250 absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white p-1 transition ease-in">
      {userType === 'GREENPOOL' && (
        <RecycleIcon className={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-500')} />
      )}
      {userType === 'POOL' && (
        <PailOutlineIcon
          className={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-500')}
        />
      )}
    </div>
  )
}

const AssetLeaseEndInfoTd: FC<{
  leaseEnd: string | null
  leaseCurrentEndingOption?: string
}> = ({ leaseEnd, leaseCurrentEndingOption }) => {
  const badge = getLeaseBadgeInfo(leaseEnd)

  return (
    <Td className="hidden sm:table-cell md:p-1 lg:p-2 xl:p-3">
      <LocalDate date={leaseEnd} />
      {leaseCurrentEndingOption && (
        <div className="mt-[0.2em] text-[0.8rem] text-gray-400">
          <Translation id={leaseCurrentEndingOption} />
        </div>
      )}

      {Boolean(badge) && badge != null && (
        <Badge dot type={badgeTypeByColor[badge.color]} size="small" className="mt-2 lowercase">
          <span className="whitespace-nowrap font-bold">
            {badge.bucket ? (
              <>
                {badge.bucket.value}+{' '}
                <Translation
                  id={
                    badge.bucket.unit === 'months'
                      ? 'months left'
                      : badge.bucket.value === 1
                        ? 'year left'
                        : 'years left'
                  }
                />
              </>
            ) : (
              <>
                {badge.days} <Translation id={badge.isOverdue ? 'days overdue' : 'days left'} />
              </>
            )}
          </span>
        </Badge>
      )}
    </Td>
  )
}
